import React, { useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Label,
  Button,
  Alert,
} from 'reactstrap';
import {
  Formik, Field, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import moment from 'moment';
import BaseGrid from '../../components/Common/BaseGrid';
import { post } from '../../helpers/api_helper';
import * as url from '../../helpers/url_helper';
import ListingSearchModal from '../../components/Offer/ListingSearchModal';

function Invoices(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t } = props;
  const [signal, setSignal] = useState(0);
  const [showListingLookup, setShowListingLookup] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);

  const tagSKUs = async (values, { resetForm, setSubmitting }) => {
    try {
      const postValues = {
        skus: values.skus.split('\n'),
      };
      const result = await post(url.POST_OFFER_LISTING_SKUS, postValues);
      toastr.success(result.message || t('SKUs tagged successfully'));
      setSignal(signal + 1);
      resetForm();
    } catch (e) {
      const result = e.response?.data;
      toastr.error(result?.error || (typeof result === 'string' ? result : e.message));
      if (Number.isInteger(result?.updated) && Number.isInteger(result?.notFound)) {
        setSignal(signal + 1);
        resetForm();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const createInvoice = async (values, { resetForm, setSubmitting }) => {
    try {
      const postValues = {
        email: values.email,
        items: [{
          sku: values.sku, quantity: values.quantity, offer_price: values.offer_price, name: selectedListing ? selectedListing.title : '',
        }],
      };
      await post(url.POST_INVOICE, postValues);
      toastr.success(t('Offer invoice created'));
      setSignal(signal + 1);
      resetForm();
    } catch (e) {
      toastr.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleListingSelect = (listing) => {
    setSelectedListing(listing);
    // Update your form or view model with the selected listing
    // For example, if using Formik: setFieldValue('listing_id', listing.id);
    setShowListingLookup(false);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="12">
            <Card>
              <CardBody>
                <CardTitle className="h4">
                  {t('Offers')}
                </CardTitle>
                <BaseGrid
                  url={url.GET_OFFERS}
                  shouldUpdate={signal}
                  config={
                  {
                    columns: {
                      id: {
                        label: t('ID'),
                        sort: true,
                        link: {
                          to: '/offers/%id%',
                        },
                      },
                      created_at: {
                        label: t('Created At'),
                        sort: true,
                        format: (x) => moment(x).format('YYYY-MM-DD HH:mm'),
                      },
                      sku: {
                        label: t('SKU'),
                        sort: true,
                      },
                      email: { label: t('Email'), sort: true },
                      asking: {
                        label: t('Asking'),
                      },
                      offer: {
                        label: t('Offer'),
                      },
                      percentage: {
                        label: t('Percentage'),
                        format: (x, item) => {
                          const askingPrice = parseFloat(item.asking);
                          const offerPrice = parseFloat(item.offer);
                          if (askingPrice > 0) {
                            const percent = ((offerPrice) / askingPrice) * 100;
                            return `${percent.toFixed(2)}%`;
                          }
                          return 'N/A';
                        },
                      },
                      currency: {
                        label: t('Currency'),
                      },
                      status: {
                        label: t('Status'),
                        format: (x, item) => {
                          if (item.counter) {
                            return t('Countered');
                          }
                          if (item.invoice_id) {
                            return t('Accepted');
                          }
                          return t('Incoming');
                        },
                      },
                    },
                    pagination: {
                      sort: 'id',
                      direction: 'desc',
                      perPage: 20,
                    },
                  }
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xxl="4">
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                {t('Create Invoice')}
              </CardHeader>
              <CardBody>
                <Formik
                  enableReinitialize
                  initialValues={{
                    sku: selectedListing ? selectedListing.sku : '',
                    email: '',
                    quantity: 1,
                    offer_price: '',
                  }}
                  validationSchema={Yup.object().shape({
                    sku: Yup.string().required(t('SKU is required')),
                    email: Yup.string().email(t('Invalid email format')).required(t('Email is required')),
                    quantity: Yup.number().integer(t('Must be a whole number')).min(1, t('Quantity must be at least 1')).required(t('Quantity is required')),
                    offer_price: Yup.number().min(0, t('Offer price must be positive')).required(t('Offer price is required')),
                  })}
                  onSubmit={createInvoice}
                >
                  {({ isSubmitting }) => (
                    <Form autoComplete="off">
                      <Row>
                        <Col md="6">
                          <div className="mb-3">
                            <Label className="col-form-label" htmlFor="sku">
                              {t('SKU')}
                            </Label>
                            <Field
                              type="text"
                              id="sku"
                              name="sku"
                              className="form-control"
                            />
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="mb-3">
                            <Label className="col-form-label d-block">&nbsp;</Label>
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => setShowListingLookup(true)}
                            >
                              {t('Lookup')}
                            </Button>
                          </div>
                        </Col>
                        <Col md="3">
                          <div className="invalid-feedback d-block">
                            <ErrorMessage name="sku" />
                          </div>
                        </Col>
                      </Row>

                      <div className="mb-3">
                        <Label className="col-form-label" htmlFor="email">
                          {t('Email')}
                        </Label>
                        <Field
                          type="email"
                          id="email"
                          name="email"
                          className="form-control"
                        />
                        <div className="invalid-feedback d-block">
                          <ErrorMessage name="email" />
                        </div>
                      </div>

                      <Row>
                        <Col md="6">
                          <div className="mb-3">
                            <Label className="col-form-label" htmlFor="quantity">
                              {t('Quantity')}
                            </Label>
                            <Field
                              type="number"
                              id="quantity"
                              name="quantity"
                              className="form-control"
                              min="1"
                            />
                            <div className="invalid-feedback d-block">
                              <ErrorMessage name="quantity" />
                            </div>
                          </div>
                        </Col>
                        <Col md="6">
                          <div className="mb-3">
                            <Label className="col-form-label" htmlFor="offer_price">
                              {`${t('Offer Price')}${selectedListing ? ` (orig. ${selectedListing.price})` : ''}`}
                            </Label>
                            <Field
                              type="number"
                              step="0.01"
                              id="offer_price"
                              name="offer_price"
                              className="form-control"
                              min="0"
                            />
                            <div className="invalid-feedback d-block">
                              <ErrorMessage name="offer_price" />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <div className="hstack gap-2 justify-content-end">
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                          <span className={`mdi mdi-content-save ${isSubmitting ? 'mdi-spin' : ''}`} />
                          {' '}
                          {t('Save Offer Invoice')}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
            <Card className="">
              <CardHeader className="bg-transparent border-bottom">{t('Tag SKUs for Offer')}</CardHeader>
              <CardBody>
                <Formik
                  enableReinitialize
                  initialValues={{ skus: '' }}
                  validationSchema={Yup.object().shape({
                    staff_note: Yup
                      .string(),
                  })}
                  onSubmit={tagSKUs}
                >
                  {({
                    errors, setFieldValue, isSubmitting,
                  }) => (
                    <Form id="SaleForm" autoComplete="off">
                      {Object.entries(errors).length > 0 && (
                        <Alert color="danger">{t('Form contains errors')}</Alert>
                      )}
                      <div className="mb-3">
                        <Field
                          className="form-control"
                          id="skus"
                          as="textarea"
                          name="skus"
                          rows={6}
                          placeholder={`${t('Enter one SKU per line (not comma-separated).')}\n\nsj-DA61-08275A\nsj-LU55V809 MAIN\nsj-W10368100\nsj-AHT73233959`}
                          onChange={(e) => { setFieldValue('skus', e.target.value); }}
                        />
                      </div>

                      <div className="hstack mb-1  justify-content-end">
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                          <span className={`mdi ${isSubmitting ? 'mdi-spin' : ''}`} />
                          {' '}
                          {t('Tag SKUs')}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>

          <Col xxl="8">
            <Card>
              <CardBody>
                <CardTitle className="h4">
                  {t('Offer Invoices')}
                </CardTitle>

                <BaseGrid
                  url={url.GET_INVOICES}
                  shouldUpdate={signal}
                  config={
                  {
                    columns: {
                      id: { label: t('ID'), sort: true },
                      sku: {
                        label: t('SKU'),
                        sort: true,
                        format: (x, item) => (item.items && item.items.length > 0
                          ? item.items[0].listing_sku : x),
                      },
                      email: { label: t('Email'), sort: true },
                      quantity: {
                        label: t('Qty'),
                        sort: true,
                        format: (x, item) => (item.items && item.items.length > 0
                          ? item.items[0].qty : x),
                      },
                      offer_price: {
                        label: t('Offer Price'),
                        sort: true,
                        format: (x, item) => (item.items && item.items.length > 0
                          ? item.items[0].price : x),
                      },
                    },
                    pagination: {
                      sort: 'id',
                      direction: 'desc',
                      perPage: 10,
                    },
                  }
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ListingSearchModal
        shown={showListingLookup}
        onClose={() => setShowListingLookup(false)}
        onSelect={handleListingSelect}
      />
    </div>
  );
}

Invoices.defaultProps = {
  t: (x) => (x),
};

Invoices.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Invoices));
